import { useCallback, useEffect, useRef, useState } from "react";
import { publicApi } from "@/lib/api";

/* OFFERS4BRICKS — bright, LEGO-inspired, iPhone-first single-page flow:
   pitch → type set numbers → CONFIRM identity → instant offer → accept → done.
   Palette: LEGO red #E3000B · yellow #FFD500 · black · white. */

import { BarcodeScanner } from "@/components/public/BarcodeScanner";
import { Link } from "react-router-dom";
import { accountApi } from "@/lib/api";
import { DRAFT_KEY } from "@/pages/public/Account";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const RED = "#E3000B", YELLOW = "#FFD500";
const money = (n) => `$${Number(n).toFixed(2)}`;

function Studs({ n = 4, color = YELLOW }) {
  return (
    <span className="inline-flex gap-1.5 align-middle">
      {Array.from({ length: n }).map((_, i) => (
        <span key={i} className="w-3.5 h-3.5 rounded-full shadow-inner" style={{ background: color, boxShadow: "inset 0 -2px 0 rgba(0,0,0,.18)" }} />
      ))}
    </span>
  );
}

function Logo({ name }) {
  return (
    <div className="flex items-center select-none" data-testid="o4b-logo">
      <img
        src="/brand/offers4bricks-logo@1000.webp"
        srcSet="/brand/offers4bricks-logo@1000.webp 1000w, /brand/offers4bricks-logo.webp 1931w"
        sizes="220px"
        alt={name}
        className="h-11 w-auto"
        draggable={false}
      />
    </div>
  );
}

function BigButton({ children, onClick, disabled, color = RED, testId, type = "button" }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testId}
      className="w-full h-14 rounded-2xl text-white text-lg font-black tracking-wide active:translate-y-px disabled:opacity-40 transition-transform"
      style={{ background: color, boxShadow: "0 6px 0 rgba(0,0,0,.18)" }}>
      {children}
    </button>
  );
}

function SetCard({ item, right, muted }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-2xl border-2 bg-white ${muted ? "border-gray-200 opacity-70" : "border-black/10"}`} data-testid={`o4b-set-${item.set_number}`}>
      <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
        {item.image_url ? <img src={item.image_url} alt={item.name || item.set_number} className="w-full h-full object-contain" /> : <span className="text-gray-400 text-xs">?</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold tracking-widest text-gray-500">#{item.set_number.replace(/-1$/, "")}</div>
        <div className="font-bold text-black leading-tight truncate">{item.name || "Not recognized"}</div>
        <div className="text-xs text-gray-500">{[item.theme, item.year, item.pieces ? `${item.pieces} pcs` : null].filter(Boolean).join(" · ")}</div>
      </div>
      {right}
    </div>
  );
}

export default function Offers4Bricks() {
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState("start"); // start → confirm → offer → accept → done
  const [input, setInput] = useState("");          // joined chips, sent to /identify
  const [chips, setChips] = useState([]);           // set numbers added so far
  const [current, setCurrent] = useState("");       // number being typed (numeric keypad)
  const [removed, setRemoved] = useState(new Set()); // sets dropped on the offer screen
  const [qty, setQty] = useState({});                // {set_number: n} — how many of each
  const qtyOf = (sn) => qty[sn] || 1;
  const bumpQty = (sn, d) => setQty(q => ({ ...q, [sn]: Math.max(1, Math.min(99, (q[sn] || 1) + d)) }));

  const chipsRef = useRef([]); chipsRef.current = chips;
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState(null);
  const importSpreadsheet = async (file) => {
    setUploading(true); setUploadNote(null);
    try {
      const r = await publicApi.parseSpreadsheet(file);
      // Match typed chips ("75192") and uploaded rows ("75192-1") as the same set.
      const bareOf = (sn) => String(sn).replace(/-1$/, "");
      const rows = r.rows.map(x => { const existing = chipsRef.current.find(c => bareOf(c) === bareOf(x.set_number)); return { ...x, key: existing || bareOf(x.set_number), existing: !!existing }; });
      const fresh = rows.filter(x => !x.existing);
      setChips(prev => { const next = [...prev, ...fresh.map(x => x.key)]; setInput(next.join(", ")); return next; });
      setQty(q => { const n = { ...q }; for (const x of rows) n[x.key] = Math.min(99, (x.existing ? (n[x.key] || 1) : 0) + (x.qty || 1)); return n; });
      setScanned(sc => { const n = { ...sc }; for (const x of rows) if (x.name) n[x.key] = x.name; return n; });
      setUploadNote({ ok: true, text: `Added ${fresh.length} set${fresh.length === 1 ? "" : "s"} from ${r.filename}${r.rows.length - fresh.length ? ` · ${r.rows.length - fresh.length} already in your list (quantities bumped)` : ""}${r.skipped?.length ? ` · ${r.skipped.length} row${r.skipped.length === 1 ? "" : "s"} skipped` : ""}` });
    } catch (e) { setUploadNote({ ok: false, text: e.response?.data?.detail || "Couldn't read that spreadsheet" }); }
    setUploading(false);
  };
  const [dupNote, setDupNote] = useState("");
  const addChips = (text) => {
    const parts = String(text).split(/[^0-9-]+/).map(t => t.trim()).filter(Boolean);
    if (!parts.length) return;
    const dups = parts.filter((p, i) => chipsRef.current.includes(p) || parts.indexOf(p) !== i);
    setChips(prev => {
      const next = [...prev];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      setInput(next.join(", "));
      return next;
    });
    if (dups.length) {
      // Same box typed twice = another copy of that set → bump its quantity instead of silently ignoring it.
      setQty(q => { const n = { ...q }; for (const d of dups) n[d] = Math.min(99, (n[d] || 1) + 1); return n; });
      setDupNote(dups.length === 1 ? `${dups[0]} was already in your list — we counted it as another copy (×${(qty[dups[0]] || 1) + 1}).` : `${dups.join(", ")} were already in your list — counted as extra copies.`);
      setTimeout(() => setDupNote(""), 4000);
    }
    setCurrent("");
  };
  const removeChip = (c) => setChips(prev => { const next = prev.filter(x => x !== c); setInput(next.join(", ")); return next; });
  const onCurrentChange = (v) => {
    // Pasting / typing a separator (space, comma, newline) commits the number as a chip
    if (/[^0-9-]/.test(v)) addChips(v); else setCurrent(v);
  };
  const [items, setItems] = useState([]);
  const [kept, setKept] = useState(new Set());
  const units = Array.from(kept).reduce((a, sn) => a + qtyOf(sn), 0);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", zip: "", city: "", state: "", notes: "", payment_method: "" });
  const [done, setDone] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState({}); // {set_number: name} from barcode scans
  const topRef = useRef(null);
  const inputRef = useRef(null);
  const payRef = useRef(null);
  const [payErr, setPayErr] = useState(false);
  // + ADD must not close the iPhone number pad: swallow the focus-stealing
  // events on the button, commit the chip, and keep the caret in the box.
  const addCurrent = () => {
    const v = (inputRef.current?.value ?? current).trim();
    if (v) addChips(v);
    inputRef.current?.focus({ preventScroll: true });
  };
  const onBarcode = useCallback(async (code) => {
    const r = await publicApi.barcode(code);
    if (r.found) {
      const sn = r.set_number.replace(/-1$/, "");
      addChips(sn);
      setScanned(prev => ({ ...prev, [sn]: r.name || "" }));
    }
    return r;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { publicApi.config().then(setCfg).catch(() => setCfg({ site_name: "OFFERS4BRICKS", headline: "Sell your sealed LEGO sets.", pitch: "", requirements: [], max_sets_per_request: 0 })); }, []);
  useEffect(() => { topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step]);

  // ---- Batch protection: autosave the in-progress list on-device, and to the
  // seller's account when signed in, so a crash/refresh never loses the sets.
  const [account, setAccount] = useState(null);
  const restored = useRef(false);
  useEffect(() => {
    accountApi.me().then(setAccount).catch(() => setAccount(null));
    const local = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    const apply = (d) => { if (d?.chips?.length && !restored.current) { restored.current = true; setChips(d.chips); setQty(d.qty || {}); setRestoredNote(true); } };
    accountApi.getDraft().then((srv) => {
      const newer = srv?.chips?.length && (!local?.chips?.length || (srv.updated_at && local?.updated_at && srv.updated_at > local.updated_at));
      apply(newer ? srv : local);
    }).catch(() => apply(local));
  }, []);
  const [restoredNote, setRestoredNote] = useState(false);
  useEffect(() => {
    if (step === "done") return;
    const draft = { chips, qty, updated_at: new Date().toISOString() };
    if (chips.length) localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); else localStorage.removeItem(DRAFT_KEY);
    if (!account) return;
    const t = setTimeout(() => { accountApi.putDraft(draft).catch(() => {}); }, 800);
    return () => clearTimeout(t);
  }, [chips, qty, account, step]);
  useEffect(() => { if (step === "done") { localStorage.removeItem(DRAFT_KEY); if (account) accountApi.clearDraft().catch(() => {}); } }, [step, account]);

  const identify = () => identifyWith(chips);
  const identifyWith = async (list) => {
    if (!list.length) return;
    setErr(""); setBusy(true);
    try {
      const r = await publicApi.identify(list.join(", "));
      setItems(r.items);
      setKept(new Set(r.items.filter(i => i.found && !i.excluded).map(i => i.set_number)));
      setStep("confirm");
    } catch (e) { setErr(e.response?.data?.detail || "Couldn't read those set numbers"); }
    setBusy(false);
  };
  const [progress, setProgress] = useState(null); // {done, total} while pricing a big batch
  const [openGroups, setOpenGroups] = useState({}); // folders of 50 on the input screen
  const getOffer = async () => {
    setErr(""); setBusy(true);
    const all = Array.from(kept);
    const qmap = Object.fromEntries(all.map(sn => [sn, qtyOf(sn)]));
    // Chunk size from Settings (capped so one request always finishes well inside the CDN's 100s limit);
    // only a few requests are in flight at once — the server throttles Keepa/PriceCharting anyway, and
    // firing 50+ long requests from a phone made the edge drop connections on big batches.
    const CHUNK = Math.min(20, Math.max(1, Number(cfg?.quote_sets_per_request) || 5));
    const IN_FLIGHT = 4;
    try {
      const chunks = [];
      for (let i = 0; i < all.length; i += CHUNK) chunks.push(all.slice(i, i + CHUNK));
      const results = new Array(chunks.length);
      let done = 0, note = null, bonus = 0, bonusLabel = null, failed = [];
      setProgress({ done: 0, total: all.length });
      const quoteOnce = (part, quoteId) => publicApi.quote(part, Object.fromEntries(part.map(sn => [sn, qmap[sn]])), quoteId);
      const run = async (idx, quoteId) => {
        const part = chunks[idx];
        let r;
        try { r = await quoteOnce(part, quoteId); }
        catch (e1) {
          if (!quoteId || e1.response?.status === 400) throw e1;
          // retry once, then fall back to one-set requests so a single hiccup never sinks the batch
          try { r = await quoteOnce(part, quoteId); }
          catch {
            const singles = await Promise.all(part.map(sn => quoteOnce([sn], quoteId).then(x => x.items).catch(() => { failed.push(sn); return []; })));
            r = { items: singles.flat(), quote_id: quoteId };
          }
        }
        results[idx] = r.items; if (r.contact_note) note = r.contact_note; done += part.length; if (r.bonus_pct) { bonus = r.bonus_pct; bonusLabel = r.bonus_label; }
        setProgress({ done, total: all.length });
        return r.quote_id;
      };
      const quoteId = await run(0, null);
      let next = 1;
      const worker = async () => { while (next < chunks.length) { const idx = next++; await run(idx, quoteId); } };
      await Promise.all(Array.from({ length: Math.min(IN_FLIGHT, Math.max(0, chunks.length - 1)) }, worker));
      const merged = results.flat();
      const total = Math.round(merged.reduce((a, i) => a + (i.line_total || 0), 0) * 100) / 100;
      setQuote({ quote_id: quoteId, items: merged, total, contact_note: note, bonus_pct: bonus, bonus_label: bonusLabel, unpriced: failed }); setStep("offer");
      if (failed.length) setErr(`${failed.length} set${failed.length === 1 ? "" : "s"} couldn't be priced right now (${failed.map(s => `#${s.replace(/-1$/, "")}`).slice(0, 6).join(", ")}${failed.length > 6 ? "…" : ""}). Tap "Fix the numbers" and try again, or submit and we'll price them by hand.`);
    } catch (e) { setErr(e.response?.data?.detail || (e.code === "ECONNABORTED" ? "That took too long — try a smaller batch or tap again." : "Couldn't build an offer right now — please tap again.")); }
    setProgress(null); setBusy(false);
  };
  const accept = async (e) => {
    e.preventDefault(); setErr("");
    if (!contact.payment_method) {
      setPayErr(true); setErr("Choose how you'd like to be paid.");
      payRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (contact.payment_method === "local" && (!contact.city.trim() || !contact.state)) {
      setPayErr(true); setErr("Tell us your city and state so we can plan the pickup.");
      payRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    try {
      const r = await publicApi.accept({ quote_id: quote.quote_id, accepted_sets: offered.map(i => i.set_number),
        quantities: Object.fromEntries(offered.map(i => [i.set_number, i.qty || 1])), ...contact });
      setDone(r); setStep("done");
    } catch (e2) { setErr(e2.response?.data?.detail || "Couldn't submit — please check your details"); }
    setBusy(false);
  };
  const reset = () => { setRemoved(new Set()); setQty({}); setStep("start"); setInput(""); setChips([]); setCurrent(""); setItems([]); setQuote(null); setDone(null); setErr(""); setScanned({}); };

  if (!cfg) return <div className="min-h-screen bg-white" />;
  const toggleRemoved = (sn) => setRemoved(prev => { const n = new Set(prev); if (n.has(sn)) n.delete(sn); else n.add(sn); return n; });
  const allOffered = (quote?.items || []).filter(i => i.offer);
  const offered = allOffered.filter(i => !removed.has(i.set_number));
  const removedItems = allOffered.filter(i => removed.has(i.set_number));
  const notOffered = (quote?.items || []).filter(i => !i.offer);
  const reviewItems = notOffered.filter(i => ["review", "high_value_review"].includes(i.status));
  const canSubmit = offered.length > 0 || reviewItems.length > 0;
  const liveTotal = offered.reduce((a, i) => a + Number(i.offer || 0) * (i.qty || 1), 0);
  const liveUnits = offered.reduce((a, i) => a + (i.qty || 1), 0);

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }} data-testid="o4b-app">
      <div ref={topRef} />
      {/* top stripe */}
      <div className="h-2 w-full" style={{ background: `linear-gradient(90deg, ${RED} 0 25%, ${YELLOW} 25% 50%, #0055BF 50% 75%, #00A850 75%)` }} />
      <header className="max-w-md mx-auto px-5 pt-5 pb-2 flex items-center justify-between">
        <Logo name={cfg.site_name} />
        <div className="flex items-center gap-3">
          <Link to="/account" className="text-xs font-bold underline" style={{ color: account ? "#00A850" : "#6B7280" }} data-testid="o4b-account-link">
            {account ? (account.name?.split(" ")[0] || "My account") : "Sign in"}
          </Link>
          {step !== "start" && <button onClick={reset} className="text-xs font-bold text-gray-500 underline" data-testid="o4b-start-over">Start over</button>}
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pb-16">
        {scanning && <BarcodeScanner onCode={onBarcode} onClose={() => setScanning(false)} count={chips.length} />}
        {step === "start" && (
          <section data-testid="o4b-step-start">
            <h1 className="text-4xl sm:text-5xl font-black leading-[1.02] tracking-tight mt-4" style={{ fontFamily: "Chivo, system-ui, sans-serif" }} data-testid="o4b-headline">
              {cfg.headline}
            </h1>
            <p className="mt-4 text-base text-gray-700 leading-relaxed" data-testid="o4b-pitch">{cfg.pitch}</p>
            <ul className="mt-4 space-y-2">
              {(cfg.requirements || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm font-semibold text-black">
                  <span className="mt-1 w-3 h-3 rounded-full shrink-0" style={{ background: i % 2 ? RED : YELLOW }} /> {r}
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-3xl p-4 border-2 border-black/10 bg-[#FFF9D6]">
              <label className="block text-xs font-black tracking-widest text-gray-600 mb-2">SET NUMBERS FROM THE BOX</label>
              {restoredNote && chips.length > 0 && (
                <div className="mb-3 rounded-2xl bg-[#FFF9D6] px-4 py-2 text-xs text-gray-700 flex items-center justify-between" data-testid="o4b-restored-note">
                  <span>Welcome back — we restored your {chips.length} saved set{chips.length === 1 ? "" : "s"}.</span>
                  <button onClick={() => { setChips([]); setQty({}); setRestoredNote(false); }} className="font-bold underline" data-testid="o4b-restored-clear">Clear</button>
                </div>
              )}
              {chips.length > 0 && (
                <div className="mb-3 space-y-2" data-testid="o4b-chips">
                  {Array.from({ length: Math.ceil(chips.length / 50) }, (_, g) => chips.slice(g * 50, g * 50 + 50)).map((group, g, groups) => {
                    const last = g === groups.length - 1;
                    const open = groups.length === 1 || openGroups[g] === true || (last && openGroups[g] !== false);
                    const chip = (c) => (
                      <button key={c} onClick={() => removeChip(c)} data-testid={`o4b-chip-${c}`} title="Tap to remove"
                        className="inline-flex items-center gap-1.5 rounded-full bg-black text-white px-3 py-1.5 text-sm font-black tracking-wide active:scale-95">
                        {c}{(qty[c] || 1) > 1 ? <span className="text-[#FFD500] text-xs font-black" data-testid={`o4b-chip-qty-${c}`}>×{qty[c]}</span> : null}{scanned[c] ? <span className="text-white/70 text-xs font-semibold truncate max-w-[110px]">{scanned[c]}</span> : null} <span className="text-white/60 text-xs">✕</span>
                      </button>
                    );
                    if (groups.length === 1) return <div key={g} className="flex flex-wrap gap-2">{group.map(chip)}</div>;
                    return (
                      <div key={g} className="rounded-2xl border-2 border-black/10 bg-white/70 overflow-hidden" data-testid={`o4b-chip-group-${g}`}>
                        <button type="button" onClick={() => setOpenGroups(o => ({ ...o, [g]: !open }))} data-testid={`o4b-chip-group-toggle-${g}`}
                          className="w-full flex items-center justify-between px-3 py-2 text-xs font-black tracking-wide text-gray-700">
                          <span>SETS {g * 50 + 1}–{g * 50 + group.length} <span className="text-gray-400 font-semibold">({group.length})</span></span>
                          <span className="text-gray-500">{open ? "Hide ▴" : "Show ▾"}</span>
                        </button>
                        {open && <div className="flex flex-wrap gap-2 px-3 pb-3">{group.map(chip)}</div>}
                      </div>
                    );
                  })}
                  <div className="text-[11px] text-gray-500 font-bold" data-testid="o4b-chips-count">{chips.length} set{chips.length === 1 ? "" : "s"} added</div>
                </div>
              )}
              <div className="flex gap-2">
                <input ref={inputRef} value={current} onChange={(e) => onCurrentChange(e.target.value)} inputMode="numeric" pattern="[0-9]*" autoCapitalize="off" autoCorrect="off"
                  enterKeyHint="done"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (current.trim()) addChips(current); else if (chips.length) identify(); } }}
                  onPaste={(e) => { const t = e.clipboardData.getData("text"); if (/[^0-9-]/.test(t)) { e.preventDefault(); addChips(t); } }}
                  placeholder={chips.length ? "Add another set number" : "e.g. 75192"}
                  className="flex-1 min-w-0 rounded-2xl border-2 border-black/15 bg-white px-4 py-3 text-xl font-bold tracking-wide focus:outline-none focus:border-black"
                  data-testid="o4b-input" />
                <button type="button" data-testid="o4b-add-btn" disabled={!current.trim()}
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchEnd={(e) => { e.preventDefault(); addCurrent(); }}
                  onClick={addCurrent}
                  className="rounded-2xl px-4 font-black text-lg border-2 border-black bg-white active:scale-95 disabled:opacity-30"
                  style={{ color: RED }}>+ ADD</button>
              </div>
              <div className="text-[11px] text-gray-500 mt-2 mb-3">{dupNote ? <b style={{ color: RED }} data-testid="o4b-dup-note">{dupNote} </b> : null}Type a number, tap <b>+ ADD</b>, repeat{cfg.max_sets_per_request > 0 ? ` · up to ${cfg.max_sets_per_request} sets` : " · as many sets as you like"} · the 4–7 digit number printed on the box</div>
              <button type="button" onClick={() => setScanning(true)} data-testid="o4b-scan-btn"
                className="w-full mb-3 h-12 rounded-2xl border-2 border-black bg-white font-black text-base tracking-wide flex items-center justify-center gap-2 active:scale-[.99]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 8v8M11 8v8M15 8v8M18 8v8" /></svg>
                Scan barcode with camera
              </button>
              <label className="w-full mb-3 h-12 rounded-2xl border-2 border-dashed border-black bg-white font-black text-base tracking-wide flex items-center justify-center gap-2 active:scale-[.99] cursor-pointer" data-testid="o4b-upload-btn">
                <input type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" data-testid="o4b-upload-input" disabled={uploading}
                  onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) await importSpreadsheet(f); }} />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4M7 9l5-5 5 5M4 20h16" /></svg>
                {uploading ? "Reading your spreadsheet…" : "Upload a spreadsheet (CSV / Excel)"}
              </label>
              {uploadNote && <div className="text-[11px] font-bold mb-3 -mt-1" style={{ color: uploadNote.ok ? "#00A850" : RED }} data-testid="o4b-upload-note">{uploadNote.text}</div>}
              <BigButton onClick={() => { const pending = current.trim(); if (pending) { addChips(pending); } identifyWith(pending ? [...chips, pending] : chips); }}
                disabled={busy || (!chips.length && !current.trim())} testId="o4b-identify-btn">
                {busy ? "Looking up…" : `Find my ${chips.length + (current.trim() ? 1 : 0) || ""} set${(chips.length + (current.trim() ? 1 : 0)) === 1 ? "" : "s"} →`}
              </BigButton>
            </div>
            <div className="mt-6 flex items-center justify-center"><Studs n={6} /></div>
          </section>
        )}

        {step === "confirm" && (
          <section data-testid="o4b-step-confirm">
            <h2 className="text-2xl font-black mt-2" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>Are these your sets?</h2>
            <p className="text-sm text-gray-600 mt-1">Tap to remove any that don't match the box in front of you. Have more than one of a set? Use <b>− / +</b>.</p>
            <div className="mt-4 space-y-3">
              {items.map(it => {
                const on = kept.has(it.set_number);
                const can = it.found && !it.excluded;
                return (
                  <button key={it.set_number} disabled={!can} onClick={() => setKept(k => { const n = new Set(k); n.has(it.set_number) ? n.delete(it.set_number) : n.add(it.set_number); return n; })}
                    className="w-full text-left" data-testid={`o4b-confirm-toggle-${it.set_number}`}>
                    <SetCard item={it} muted={!on || !can} right={
                      !it.found ? <span className="text-[11px] font-black text-white px-2 py-1 rounded-lg" style={{ background: RED }}>NOT FOUND</span>
                        : it.excluded ? <span className="text-[11px] font-black text-gray-600 px-2 py-1 rounded-lg bg-gray-200">NOT BUYING</span>
                        : <span className="flex flex-col items-end gap-1.5">
                            <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center font-black ${on ? "text-white border-transparent" : "border-gray-300 text-transparent"}`} style={{ background: on ? "#00A850" : "white" }}>✓</span>
                            {on && (
                              <span className="inline-flex items-center rounded-full border-2 border-black/15 bg-white overflow-hidden" data-testid={`o4b-qty-${it.set_number}`}
                                onClick={(e) => e.stopPropagation()}>
                                <span role="button" onClick={(e) => { e.stopPropagation(); bumpQty(it.set_number, -1); }} data-testid={`o4b-qty-minus-${it.set_number}`}
                                  className="w-8 h-8 flex items-center justify-center font-black text-lg text-gray-700 active:bg-gray-100 select-none">−</span>
                                <span className="min-w-[2rem] text-center font-black text-sm" data-testid={`o4b-qty-value-${it.set_number}`}>×{qtyOf(it.set_number)}</span>
                                <span role="button" onClick={(e) => { e.stopPropagation(); bumpQty(it.set_number, +1); }} data-testid={`o4b-qty-plus-${it.set_number}`}
                                  className="w-8 h-8 flex items-center justify-center font-black text-lg active:bg-gray-100 select-none" style={{ color: RED }}>+</span>
                              </span>
                            )}
                          </span>
                    } />
                  </button>
                );
              })}
            </div>
            {err && <div className="mt-3 text-sm font-bold" style={{ color: RED }} data-testid="o4b-error">{err}</div>}
            <div className="mt-6 space-y-3">
              <BigButton onClick={getOffer} disabled={busy || kept.size === 0} testId="o4b-get-offer-btn">{busy ? (progress ? `Pricing ${progress.done}/${progress.total} sets…` : "Pricing your sets…") : `Get my offer for ${units} set${units === 1 ? "" : "s"} →`}</BigButton>
              {progress && (
                <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden" data-testid="o4b-quote-progress">
                  <div className="h-full transition-[width] duration-300" style={{ width: `${Math.round(progress.done / progress.total * 100)}%`, background: "#00A850" }} />
                </div>
              )}
              <button onClick={() => setStep("start")} className="w-full h-12 rounded-2xl font-bold text-gray-700 border-2 border-gray-200" data-testid="o4b-back-btn">← Fix the numbers</button>
            </div>
          </section>
        )}

        {step === "offer" && quote && (
          <section data-testid="o4b-step-offer">
            <div className="rounded-3xl p-5 text-white mt-2" style={{ background: RED }}>
              <div className="text-xs font-black tracking-widest opacity-80">YOUR INSTANT OFFER</div>
              <div className="text-5xl font-black mt-1" style={{ fontFamily: "Chivo, system-ui, sans-serif" }} data-testid="o4b-total">{money(liveTotal)}</div>
              <div className="text-sm opacity-90 mt-1">{liveUnits} set{liveUnits === 1 ? "" : "s"} · sealed · paid on receipt</div>
              {quote.bonus_pct ? <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-black" style={{ background: YELLOW, color: "#000" }} data-testid="o4b-bonus-badge">★ {quote.bonus_label || "Loyalty bonus"} · +{quote.bonus_pct}% included</div> : null}
            </div>
            <div className="mt-4 space-y-3">
              {offered.map(it => (
                <SetCard key={it.set_number} item={it} right={
                  <span className="flex flex-col items-end gap-1">
                    <span className="font-black text-lg" data-testid={`o4b-offer-${it.set_number}`}>{money(it.offer * (it.qty || 1))}</span>
                    {(it.qty || 1) > 1 && <span className="text-[11px] font-bold text-gray-500" data-testid={`o4b-offer-qty-${it.set_number}`}>{money(it.offer)} × {it.qty}</span>}
                    <button onClick={() => toggleRemoved(it.set_number)} data-testid={`o4b-remove-${it.set_number}`}
                      className="text-[11px] font-black tracking-wide px-2 py-1 rounded-full border-2 border-black/15 text-gray-600 active:scale-95">✕ REMOVE</button>
                  </span>
                } />
              ))}
              {removedItems.map(it => (
                <SetCard key={it.set_number} item={it} muted right={
                  <button onClick={() => toggleRemoved(it.set_number)} data-testid={`o4b-undo-${it.set_number}`}
                    className="text-[11px] font-black tracking-wide px-2 py-1 rounded-full border-2 text-white active:scale-95" style={{ background: RED, borderColor: RED }}>UNDO</button>
                } />
              ))}
              {notOffered.map(it => <SetCard key={it.set_number} item={it} muted right={<span className="text-[11px] font-bold text-gray-600 text-right max-w-[120px]">{it.message}</span>} />)}
            </div>
            <p className="text-xs text-gray-500 mt-4">{quote.contact_note}</p>
            <div className="mt-5 space-y-3">
              <BigButton onClick={() => setStep("accept")} disabled={!canSubmit} color="#00A850" testId="o4b-accept-btn">
                {offered.length > 0 ? "Accept offer →" : "Request my custom offer →"}
              </BigButton>
              <button onClick={() => setStep("confirm")} className="w-full h-12 rounded-2xl font-bold text-gray-700 border-2 border-gray-200">← Change sets</button>
            </div>
          </section>
        )}

        {step === "accept" && quote && (
          <form onSubmit={accept} data-testid="o4b-step-accept">
            <h2 className="text-2xl font-black mt-2" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>Great — where do we reach you?</h2>
            <p className="text-sm text-gray-600 mt-1">
              {offered.length > 0 ? <>Locking in <b>{money(liveTotal)}</b> for {liveUnits} set{liveUnits === 1 ? "" : "s"}.</> : null}
              {reviewItems.length > 0 && <> {offered.length > 0 ? "Plus " : ""}{reviewItems.length} set{reviewItems.length === 1 ? "" : "s"} we'll price personally and send you a custom offer for.</>}
            </p>
            <div className="mt-4 space-y-3">
              <div ref={payRef} className={`rounded-2xl p-3 border-2 ${payErr ? "border-[#E3000B] bg-[#FFF1F1]" : "border-black/10 bg-[#FAFAF7]"}`} data-testid="o4b-payment-block">
                <div className="text-xs font-black tracking-widest text-gray-700 mb-2">HOW SHOULD WE PAY YOU? <span style={{ color: RED }}>*</span></div>
                <div className={`grid gap-2 ${cfg?.local_pickup_enabled !== false ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`} data-testid="o4b-payment-methods">
                  {[["venmo", "Venmo", "#008CFF"], ["cashapp", "Cash App", "#00D632"], ["paypal", "PayPal", "#003087"], ...(cfg?.local_pickup_enabled !== false ? [["local", "Local / Cash", "#111111"]] : [])].map(([id, label, color]) => {
                    const on = contact.payment_method === id;
                    return (
                      <button type="button" key={id} onClick={() => { setContact(c => ({ ...c, payment_method: id })); setPayErr(false); setErr(""); }} data-testid={`o4b-pay-${id}`}
                        className={`h-14 rounded-2xl border-2 font-black text-sm active:scale-95 transition-colors ${on ? "text-white" : "bg-white text-gray-800 border-black/15"}`}
                        style={on ? { background: color, borderColor: color } : undefined}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {contact.payment_method === "local" ? (
                  <div className="mt-3 space-y-2" data-testid="o4b-local-block">
                    <div className="text-[12px] text-gray-700 leading-snug" data-testid="o4b-local-note">{cfg?.local_pickup_note || "Close by, or a big collection? We can arrange a local pickup and pay you in cash on the spot."}</div>
                    <div className="grid grid-cols-[1fr_120px] gap-2">
                      <input type="text" placeholder="City" value={contact.city} onChange={(e) => setContact(c => ({ ...c, city: e.target.value }))} required
                        className="h-12 rounded-2xl border-2 border-black/15 bg-white px-4 text-base font-semibold focus:outline-none focus:border-black" data-testid="o4b-contact-city" />
                      <select value={contact.state} onChange={(e) => setContact(c => ({ ...c, state: e.target.value }))} required
                        className="h-12 rounded-2xl border-2 border-black/15 bg-white px-3 text-base font-semibold focus:outline-none focus:border-black" data-testid="o4b-contact-state">
                        <option value="">State</option>
                        {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500 mt-2">{payErr ? <b style={{ color: RED }}>Tap one to continue. </b> : null}We'll ask for your {contact.payment_method === "cashapp" ? "$cashtag" : contact.payment_method === "paypal" ? "PayPal email" : "@handle"} when we confirm your sets.</div>
                )}
              </div>
              {[["name", "Your name", "text"], ["email", "Email", "email"], ["phone", "Phone (optional)", "tel"], ["zip", "ZIP code", "text"]].map(([k, label, type]) => (
                <input key={k} type={type} required={k === "name" || k === "email"} placeholder={label} value={contact[k]}
                  onChange={(e) => setContact(c => ({ ...c, [k]: e.target.value }))}
                  className="w-full h-13 rounded-2xl border-2 border-black/15 px-4 py-3 text-base font-semibold focus:outline-none focus:border-black" data-testid={`o4b-contact-${k}`} />
              ))}
              <textarea placeholder="Anything we should know? (optional)" rows={2} value={contact.notes} onChange={(e) => setContact(c => ({ ...c, notes: e.target.value }))}
                className="w-full rounded-2xl border-2 border-black/15 px-4 py-3 text-base focus:outline-none focus:border-black" data-testid="o4b-contact-notes" />
            </div>
            {err && <div className="mt-3 text-sm font-bold" style={{ color: RED }} data-testid="o4b-error">{err}</div>}
            <div className="mt-5"><BigButton type="submit" disabled={busy} color="#00A850" testId="o4b-submit-btn">{busy ? "Sending…" : (offered.length > 0 ? "Lock in my offer" : "Send my request")}</BigButton></div>
          </form>
        )}

        {step === "done" && done && (
          <section className="text-center pt-10" data-testid="o4b-step-done">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center text-white text-4xl font-black" style={{ background: "#00A850" }}>✓</div>
            <h2 className="text-3xl font-black mt-5" style={{ fontFamily: "Chivo, system-ui, sans-serif" }}>You're all set!</h2>
            <p className="text-gray-700 mt-2">
              {done.count > 0 ? <>We've locked in <b>{money(done.total)}</b> for {done.count} set{done.count === 1 ? "" : "s"}. </> : null}
              {done.review_count > 0 ? <>We'll personally review {done.review_count} high-value set{done.review_count === 1 ? "" : "s"} and email you a custom offer. </> : null}
              {cfg.contact_note}
            </p>
            <p className="text-xs text-gray-400 mt-3">Order {done.order_no} · Reference {done.request_id}</p>
            <div className="mt-6 space-y-3">
              {done.email_sent_to && <p className="text-sm text-gray-700" data-testid="o4b-email-note">We've emailed a copy of your invoice to <b>{done.email_sent_to}</b>.</p>}
              <BigButton onClick={() => window.open(done.invoice_path, "_blank")} testId="o4b-invoice-btn">Print my invoice →</BigButton>
              <p className="text-[11px] text-gray-500">Save this private link to reopen your invoice anytime:<br />
                <a href={done.invoice_path} className="underline break-all font-mono" data-testid="o4b-invoice-link">{window.location.origin}{done.invoice_path}</a></p>
            </div>
            <div className="mt-8"><BigButton onClick={reset} color="#111" testId="o4b-again-btn">Sell more sets</BigButton></div>
          </section>
        )}
      </main>
      <footer className="max-w-md mx-auto px-5 pb-8 text-[10px] text-gray-400 text-center">
        LEGO® is a trademark of the LEGO Group, which does not sponsor or endorse this site.
      </footer>
    </div>
  );
}
